import axios, { AxiosInstance, AxiosError } from 'axios';



class ApiService {
  private api: AxiosInstance;

  constructor() {
    console.log('API URL Configured:', process.env.REACT_APP_API_URL);
    this.api = axios.create({
      baseURL: process.env.REACT_APP_API_URL || 'https://bhadwa.app/api', // Prioritize environment variable, fallback to custom domain
      headers: {
        'Content-Type': 'application/json',
      },
    });

    // Request interceptor - add token
    this.api.interceptors.request.use(
      (config) => {
        const token = localStorage.getItem('access_token');
        if (token) {
          config.headers.Authorization = `Bearer ${token}`;
        }
        return config;
      },
      (error) => Promise.reject(error)
    );

    // Response interceptor - handle token refresh
    this.api.interceptors.response.use(
      (response) => response,
      async (error: AxiosError) => {
        const originalRequest: any = error.config;

        if (error.response?.status === 401 && !originalRequest._retry) {
          // Check if user has tokens (is authenticated)
          const hasTokens = !!localStorage.getItem('access_token') || !!localStorage.getItem('refresh_token');
          
          // Only redirect to login if user WAS authenticated but lost session
          if (hasTokens) {
            originalRequest._retry = true;

            try {
              const refreshToken = localStorage.getItem('refresh_token');

              const response = await axios.post(`${process.env.REACT_APP_API_URL || 'https://bhadwa.app/api'}/token/refresh/`, {
                refresh: refreshToken,
              });

              localStorage.setItem('access_token', response.data.access);
              this.api.defaults.headers.common['Authorization'] = `Bearer ${response.data.access}`;
              return this.api(originalRequest);
            } catch (refreshError) {
              localStorage.removeItem('access_token');
              localStorage.removeItem('refresh_token');
              window.location.href = '/login';
              return Promise.reject(refreshError);
            }
          } else {
            // Guest user - don't redirect, just reject the error
            return Promise.reject(error);
          }
        }

        return Promise.reject(error);
      }
    );
  }

  // Auth
  // [POST] /auth/login/ (login)
  async login(email: string, password: string) {
    const response = await this.api.post('/auth/login/', { email, password });
    if (response.data.access) {
      localStorage.setItem('access_token', response.data.access);
      localStorage.setItem('refresh_token', response.data.refresh);
    }
    return response.data;
  }

  // [POST] /auth/register/ (register)
  async register(data: any) {
    const response = await this.api.post('/auth/register/', data);
    return response.data;
  }

  // [POST] /auth/logout/ (logout)
  async logout() {
    const refreshToken = localStorage.getItem('refresh_token');
    await this.api.post('/auth/logout/', { refresh: refreshToken });
    localStorage.removeItem('access_token');
    localStorage.removeItem('refresh_token');
  }

  // [GET] /auth/me/ (get current user)
  async getCurrentUser() {
    const response = await this.api.get('/auth/me/');
    return response.data;
  }

  // [POST] /auth/password-reset/ (forgot password)
  async forgotPassword(email: string, mobileNumber?: string) {
    const response = await this.api.post('/auth/password-reset/', { 
      email, 
      mobile_number: mobileNumber 
    });
    return response.data;
  }

  // [POST] /auth/password-reset/confirm/ (reset password)
  async resetPassword(uid: string, token: string, password: string) {
    const response = await this.api.post('/auth/password-reset/confirm/', { uid, token, new_password: password });
    return response.data;
  }

  // Products
  // [GET] /products/ (get products)
  async getProducts(params?: any) {
    const response = await this.api.get('/products/', { params });
    return response.data;
  }

  // [GET] /products/:id/ (get product)
  async getProduct(id: number) {
    const response = await this.api.get(`/products/${id}/`);
    return response.data;
  }

  // [GET] /products/search/?q= (search products)
  async searchProducts(query: string) {
    const response = await this.api.get(`/products/search/?q=${query}`);
    return response.data;
  }

  // Cart
  // [GET] /cart/ (get cart)
  async getCart() {
    const response = await this.api.get('/cart/');
    return response.data;
  }

  // [POST] /cart/items/ (add to cart)
  async addToCart(productId: number, quantity: number = 1, selectedSize?: string) {
    const response = await this.api.post('/cart/items/', {
      product: productId,
      quantity,
      selected_size: selectedSize || '',
    });
    return response.data;
  }

  // [PATCH] /cart/items/:itemId/update/ (update cart item)
  async updateCartItem(itemId: number, quantity: number) {
    const response = await this.api.patch(`/cart/items/${itemId}/update/`, {
      quantity,
    });
    return response.data;
  }

  // [DELETE] /cart/items/:itemId/ (remove from cart)
  async removeFromCart(itemId: number) {
    const response = await this.api.delete(`/cart/items/${itemId}/`);
    return response.data;
  }

  // [DELETE] /cart/clear/ (clear cart)
  async clearCart() {
    const response = await this.api.delete('/cart/clear/');
    return response.data;
  }

  // Orders
  // [POST] /orders/create/ (create order)
  async createOrder(data: any) {
    const response = await this.api.post('/orders/create/', data);
    return response.data;
  }

  // [GET] /orders/ (get orders)
  async getOrders() {
    const response = await this.api.get('/orders/');
    return response.data;
  }

  // [GET] /orders/:id/ (get order)
  async getOrder(id: number) {
    const response = await this.api.get(`/orders/${id}/`);
    return response.data;
  }

  // [POST] /orders/:id/cancel/ (cancel order)
  async cancelOrder(id: number) {
    const response = await this.api.post(`/orders/${id}/cancel/`);
    return response.data;
  }

  // [POST] /orders/guest-checkout/ (guest checkout - no auth required)
  async guestCheckout(data: any) {
    try {
      // Create a separate axios instance without auth interceptor for guest checkout
      const guestApi = axios.create({
        baseURL: process.env.REACT_APP_API_URL || 'https://bhadwa.app/api',
        headers: {
          'Content-Type': 'application/json',
        },
      });
      console.log('Guest checkout request data:', data);
      const response = await guestApi.post('/orders/guest-checkout/', data);
      console.log('Guest checkout response:', response.data);
      return response.data;
    } catch (error: any) {
      console.error('Guest checkout error:', error);
      console.error('Error response:', error.response);
      console.error('Error message:', error.message);
      console.error('Error code:', error.code);
      
      if (error.response) {
        // Server responded with error
        throw {
          status: error.response.status,
          data: error.response.data,
          detail: error.response.data?.detail || error.response.data?.message || error.response.statusText,
        };
      } else if (error.request) {
        // Request made but no response
        throw {
          status: 0,
          detail: 'No response from server. Please check your internet connection and try again.',
        };
      } else {
        // Error in request setup
        throw {
          status: 0,
          detail: error.message || 'Failed to place order. Please try again.',
        };
      }
    }
  }

  // [GET] /orders/track/<id>/?email=<email> (get guest order - no auth required)
  async getGuestOrder(orderId: number, email: string) {
    // Create a separate axios instance without auth interceptor for guest tracking
    const guestApi = axios.create({
      baseURL: process.env.REACT_APP_API_URL || 'https://bhadwa.app/api',
      headers: {
        'Content-Type': 'application/json',
      },
    });
    const response = await guestApi.get(`/orders/track/${orderId}/?email=${email}`);
    return response.data;
  }

  // Payment (Razorpay)
  // [POST] /payments/razorpay/create-order/ (create payment order)
  async createRazorpayOrder(orderId: number) {
    const response = await this.api.post('/payments/razorpay/create-order/', {
      order_id: orderId,
    });
    return response.data;
  }

  // [POST] /payments/razorpay/verify/ (verify payment)
  async verifyRazorpayPayment(data: any) {
    const response = await this.api.post('/payments/razorpay/verify/', data);
    return response.data;
  }

  // [GET] /payments/razorpay/config/ (get razorpay config/keys)
  async getRazorpayConfig() {
    const response = await this.api.get('/payments/razorpay/config/');
    return response.data;
  }

  // Wishlist
  // [GET] /wishlist/ (get wishlist)
  async getWishlist() {
    const response = await this.api.get('/wishlist/');
    // Extract items from the response (it returns wishlist object with items array)
    return response.data.items || response.data;
  }

  // [POST] /wishlist/add/ (add to wishlist)
  async addToWishlist(productId: number) {
    const response = await this.api.post('/wishlist/add/', {
      product_id: productId,
    });
    return response.data;
  }

  // [POST] /wishlist/remove/ (remove from wishlist)
  async removeFromWishlist(productId: number) {
    const response = await this.api.post('/wishlist/remove/', {
      product_id: productId,
    });
    return response.data;
  }

  // [GET] /wishlist/check/?product_id= (check wishlist)
  async checkWishlist(productId: number) {
    const response = await this.api.get(`/wishlist/check/?product_id=${productId}`);
    return response.data;
  }

  // Reviews
  // [GET] /reviews/?product= (get product reviews)
  async getProductReviews(productId: number) {
    const response = await this.api.get(`/reviews/?product=${productId}`);
    // Handle paginated response
    return response.data.results || response.data;
  }

  // [POST] /reviews/ (create review)
  async createReview(data: any) {
    const response = await this.api.post('/reviews/', data);
    return response.data;
  }

  // [PUT] /reviews/:id/ (update review)
  async updateReview(id: number, data: any) {
    const response = await this.api.put(`/reviews/${id}/`, data);
    return response.data;
  }

  // [DELETE] /reviews/:id/ (delete review)
  async deleteReview(id: number) {
    const response = await this.api.delete(`/reviews/${id}/`);
    return response.data;
  }

  // [POST] /reviews/:id/mark_helpful/ (mark review helpful)
  async markReviewHelpful(id: number) {
    const response = await this.api.post(`/reviews/${id}/mark_helpful/`);
    return response.data;
  }

  // Addresses
  // [GET] /addresses/ (get addresses)
  async getAddresses() {
    const response = await this.api.get('/addresses/');
    return response.data;
  }

  // [POST] /addresses/ (create address)
  async createAddress(data: any) {
    const response = await this.api.post('/addresses/', data);
    return response.data;
  }

  // [PUT] /addresses/:id/ (update address)
  async updateAddress(id: number, data: any) {
    const response = await this.api.put(`/addresses/${id}/`, data);
    return response.data;
  }

  // [DELETE] /addresses/:id/ (delete address)
  async deleteAddress(id: number) {
    const response = await this.api.delete(`/addresses/${id}/`);
    return response.data;
  }

  // Categories
  // [GET] /categories/ (get categories)
  async getCategories() {
    const response = await this.api.get('/categories/');
    return response.data;
  }

  // Profile
  // [PUT] /profile/update/ (update profile)
  async updateProfile(data: any) {
    const response = await this.api.put('/profile/update/', data);
    return response.data;
  }

  // [POST] /auth/change-password/ (change password)
  async changePassword(data: any) {
    const response = await this.api.post('/auth/change-password/', data);
    return response.data
  }
}

export default new ApiService();
